import React from 'react';
import { useTheme } from '../context/ThemeContext';
import type { EventPreview } from '../pages/EventInvitePage';
import EventInviteCta from './EventInviteCta';
import EventRequirements from './EventRequirements';
import FeatureIcon, { FeatureIconName } from './FeatureIcon';
import { EVENT_TYPES, EventTypeId } from './canonicalAccents';
import {
  buildEventCharacteristics,
  CharacteristicTone,
} from '../lib/eventCharacteristics';
import './EventCard.css';

interface Props {
  event: EventPreview;
  inviteCode?: string;
}

const formatDate = (isoDate: string | null): string => {
  if (!isoDate) return 'Date to be announced';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return 'Date to be announced';

  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatTime = (isoDate: string | null): string => {
  if (!isoDate) return 'Time to be announced';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return 'Time to be announced';

  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const EventInviteCard: React.FC<Props> = ({ event, inviteCode }) => {
  const { colors, dsColors } = useTheme();

  const isInviteOnly = !event.isPublic;
  const typeId = (event.eventType || '').toLowerCase() as EventTypeId;
  const typeIdentity = EVENT_TYPES[typeId] ?? null;
  const typeColor = typeIdentity?.color || colors.textTertiary;
  const isTerminal = event.status === 'cancelled' || event.status === 'completed';
  const { chips, requirements } = buildEventCharacteristics(event);
  const lockedRoute = event.lockedRoute;
  const showLockedRoute =
    (typeId === 'cruise' || typeId === 'tour') &&
    !!lockedRoute &&
    (lockedRoute.destinationLocked === true || lockedRoute.waypointsLocked === true);

  const chipTone: Record<CharacteristicTone, string> = {
    neutral: colors.textTertiary,
    info: dsColors.signalBlue,
    success: dsColors.successStrong,
    warning: dsColors.warningStrong,
  };

  const fact = (icon: FeatureIconName, label: string, value: React.ReactNode) => (
    <div className="flex items-center gap-3" key={label}>
      <span style={{ color: colors.textTertiary }} aria-hidden="true">
        <FeatureIcon name={icon} size={18} />
      </span>
      <div className="flex flex-col" style={{ minWidth: 0 }}>
        <span
          className="text-[11px] uppercase tracking-wide"
          style={{ color: colors.textTertiary }}
        >
          {label}
        </span>
        <span className="text-sm" style={{ color: colors.textPrimary }}>
          {value}
        </span>
      </div>
    </div>
  );

  return (
    <div className="shared-event-shell">
      <article
        className="shared-event"
        style={{
          '--event-panel-surface': colors.surface,
          '--event-panel-border': colors.border,
        } as React.CSSProperties}
      >
        <header className="shared-event__story">
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${typeColor}1f`, color: typeColor }}
              aria-hidden="true"
            >
              <FeatureIcon name={typeIdentity?.icon || 'calendar'} size={24} />
            </span>
            <div className="flex items-center gap-2 flex-wrap">
              {event.eventType && (
                <span
                  className="text-xs font-semibold capitalize"
                  style={{ color: typeIdentity ? typeColor : colors.textPrimary }}
                >
                  {event.eventType}
                </span>
              )}
              <span
                className="text-xs uppercase tracking-wide"
                style={{ color: colors.textTertiary }}
              >
                {isInviteOnly ? 'Invite-only event' : 'Public event'}
              </span>
            </div>
          </div>
          <h1 className="shared-event__title" style={{ color: colors.textPrimary }}>
            {event.title || 'RidePulse Event'}
          </h1>
          {event.host && (
            <div className="flex items-center gap-3 mt-3">
              {event.host.avatarURL && (
                <img
                  src={event.host.avatarURL}
                  alt=""
                  className="w-8 h-8 rounded-full object-cover border border-slate-600"
                />
              )}
              <p className="text-sm m-0" style={{ color: colors.textSecondary }}>
                Hosted by{' '}
                <span className="font-medium" style={{ color: colors.textPrimary }}>
                  {event.host.displayName || 'RidePulse host'}
                </span>
              </p>
            </div>
          )}
          {chips.length > 0 && (
            <ul className="shared-event__chips">
              {chips.map((chip) => (
                <li
                  key={chip.id}
                  className="shared-event__chip"
                  style={{ color: chipTone[chip.tone], borderColor: colors.border }}
                >
                  <FeatureIcon name={chip.icon} size={14} />
                  <span>{chip.label}</span>
                </li>
              ))}
            </ul>
          )}
          {isTerminal && (
            <p
              className="mt-3 text-xs font-medium rounded-lg px-3 py-2 inline-block"
              role="status"
              style={{ backgroundColor: `${dsColors.error}1f`, color: dsColors.error }}
            >
              {event.status === 'cancelled'
                ? 'This event was cancelled.'
                : 'This event has ended.'}
            </p>
          )}
        </header>

        <div
          className={`shared-event__information${requirements.length ? '' : ' shared-event__information--without-requirements'}`}
        >
          <aside className="shared-event__details shared-event__panel">
            <h2
              className="text-[11px] uppercase tracking-wide m-0 mb-3"
              style={{ color: colors.textTertiary }}
            >
              Event details
            </h2>
            <dl className="shared-event__details-facts">
              <div>
                {fact('calendar', 'Date', formatDate(event.meetupTime || event.date))}
              </div>
              <div>
                {fact('clock', 'Meetup', formatTime(event.meetupTime || event.date))}
              </div>
              {event.ksuTime && (
                <div>{fact('road', 'Departure', formatTime(event.ksuTime))}</div>
              )}
              {event.location?.name && (
                <div>
                  {fact(
                    'pin',
                    'Start / meetup point',
                    <>
                      {event.location.name}
                      {event.location.address && (
                        <span
                          className="block text-xs"
                          style={{ color: colors.textSecondary }}
                        >
                          {event.location.address}
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}
            </dl>
            {showLockedRoute && (
              <div
                className="shared-event__route-locked rounded-xl px-3 py-2"
                style={{ border: `1px dashed ${colors.border}` }}
              >
                {lockedRoute!.waypointsLocked === true && (
                  <p className="flex items-center gap-2 text-xs m-0" style={{ color: colors.textSecondary }}>
                    <FeatureIcon name="shield" size={14} />
                    <span>Waypoints: Locked</span>
                  </p>
                )}
                {lockedRoute!.destinationLocked === true && (
                  <p className="flex items-center gap-2 text-xs m-0 mt-1" style={{ color: colors.textSecondary }}>
                    <FeatureIcon name="shield" size={14} />
                    <span>Destination: Locked</span>
                  </p>
                )}
                <p className="text-xs m-0 mt-2" style={{ color: colors.textTertiary }}>
                  Check in at the meetup in RidePulse to see the destination and stops.
                </p>
              </div>
            )}
          </aside>
          <EventRequirements requirements={requirements} />
          {/* inviteCode is a launch credential — dispatch targets only, never rendered. */}
          <div className="shared-event__app-action">
            {isInviteOnly && inviteCode !== undefined && (
              <p className="text-xs m-0 mb-2" style={{ color: colors.textSecondary }}>
                This is an invitation preview. Viewing it does not join the
                event. Open RidePulse to review and confirm joining.
              </p>
            )}
            <EventInviteCta event={event} inviteCode={inviteCode} />
          </div>
        </div>

        {event.description && (
          <section className="shared-event__description">
            <p
              className="shared-event__description-text text-base m-0"
              style={{ color: colors.textSecondary, lineHeight: 1.7 }}
            >
              {event.description}
            </p>
          </section>
        )}

        {event.flyerUrl && (
          <figure className="shared-event__media">
            <img src={event.flyerUrl} alt={`Flyer for ${event.title || 'the event'}`} />
          </figure>
        )}
      </article>
    </div>
  );
};

export default EventInviteCard;
